import * as React from "react";
import { Alert, Box, Snackbar, Stack, Typography } from "@mui/material";
import { colors } from "../../styles/constants";
import { Header, Subheader } from "../../components/atoms/Header";
import { Accordion } from "../../components/molecules/Accordion";
import { SimpleUploadField } from "../../components/organisms/SimpleUploadField";
import { UpdateFormTemplateProc } from "@/templates/FormTemplate";
import { RequiredHeader } from "@/components/molecules/RequiredHeader";
import { ExpressDocumentsRecord } from "@/types/Document";
import { UploadedDocument } from "@/types/Document";

const currentSection = "documents";

export type DocumentsRecord = {
  readonly proofOfAddress: UploadedDocument[];
  readonly proofOfDeath: UploadedDocument[];
  readonly willAndProbate: UploadedDocument[];
  readonly grantOfRepresentation: UploadedDocument[];
};

export type DocumentsViewProps = {
  readonly serviceProvider: { customForm?: string };
  askForProofOfAddress?: boolean;
  askForWillAndProbate?: boolean;
  askForGrantOfRepresentation?: boolean;
  askForProofOfBankAccount?: boolean;
  busy: boolean;
  record: ExpressDocumentsRecord;
  errors: any;
  hasChanges: boolean;
  remoteError?: string;
  setError: any;
  updateProofOfAddress: any;
  removeProofOfAddress: any;
  updateProofOfDeath: any;
  removeProofOfDeath: any;
  updateWillAndProbate: any;
  removeWillAndProbate: any;
  updateGrantOfRepresentation: any;
  removeGrantOfRepresentation: any;
  updateProofOfBankAccount: any;
  removeProofOfBankAccount: any;
  onNext: any;
  onSectionClick: (section: string) => void;
  updateTemplate: UpdateFormTemplateProc;
  removeRemoteError: () => void;
  showAGOROptional?: boolean;
};

export const DocumentsView: React.FC<DocumentsViewProps> = ({
  serviceProvider,
  askForProofOfAddress,
  askForWillAndProbate,
  askForGrantOfRepresentation,
  askForProofOfBankAccount,
  busy,
  record,
  errors,
  hasChanges,
  remoteError,
  setError,
  updateProofOfAddress,
  removeProofOfAddress,
  updateWillAndProbate,
  removeWillAndProbate,
  updateGrantOfRepresentation,
  removeGrantOfRepresentation,
  updateProofOfBankAccount,
  removeProofOfBankAccount,
  updateProofOfDeath,
  removeProofOfDeath,
  onNext,
  onSectionClick,
  updateTemplate,
  removeRemoteError,
  showAGOROptional,
}) => {
  React.useEffect(() => {
    updateTemplate({
      busy,
      currentSection,
      onNext,
      saveLabel: null,
      onSectionClick,
    });
  }, [busy, onNext, onSectionClick, updateTemplate, hasChanges]);

  return (
    <Stack rowGap={4}>
      <Stack rowGap={2}>
        <Header level={1} color={colors.accentTeal}>
          Your documents
        </Header>

        <Box>
          <Subheader faded>Top tip</Subheader>
          <Typography variant="body1">
            When uploading photographs or scans of documents, please make sure that the image
            contains <b>all four corners of the page</b> and that{" "}
            <b>all text is clear and legible</b>.
          </Typography>
        </Box>
      </Stack>
      <Stack rowGap={2}>
        <Accordion
          teal
          expanded={record.proofOfDeath.length !== 0}
          title={
            <RequiredHeader
              showError={!!errors.proofOfDeath || record.proofOfDeath.length === 0}
              title="Death certificate"
            />
          }
        >
          <Stack rowGap={1}>
            <SimpleUploadField
              busy={false}
              entries={record.proofOfDeath}
              upload={updateProofOfDeath}
              remove={removeProofOfDeath}
              error={errors.proofOfDeath}
              setError={(error: any) => setError("proofOfDeath", error)}
            />

            <Typography variant="body1">
              By uploading a file, you are confirming that you certify that it is a <b>true</b> and
              {"  "}
              <b>correct</b> copy of the death certificate.
            </Typography>

            <Typography variant="body1">
              Please upload the death certificate. If a death certificate is not available, please
              upload the <b>Coroner’s Interim Certificate</b>.
            </Typography>

            <Typography variant="body1">
              Some companies may require a physical copy to be sent instead of a certified copy.
            </Typography>
          </Stack>
        </Accordion>

        {askForProofOfAddress && (
          <Accordion
            expanded={record.proofOfAddress.length !== 0}
            teal
            title={
              <RequiredHeader
                showError={!!errors.proofOfAddress || record.proofOfAddress.length === 0}
                title="Proof of your address"
              />
            }
          >
            <Stack rowGap={1}>
              <SimpleUploadField
                busy={false}
                entries={record.proofOfAddress}
                upload={updateProofOfAddress}
                remove={removeProofOfAddress}
                error={errors.proofOfAddress}
                setError={(error: any) => setError("proofOfAddress", error)}
              />

              <Typography variant="body1">
                Please upload a <b>Council Tax Bill/Demand Letter</b> (issued within the last 12
                months) <b>OR</b> a <b>Utility Bill</b> (issued within the last 3 months.)
              </Typography>

              <div>
                <Typography variant="body1">
                  If you don’t have either of these documents, please upload one of the following:
                </Typography>

                <ul style={{ margin: 0 }}>
                  <li>
                    A Bank, Building Society or Credit Card Statement (issued within the last 3
                    months)
                  </li>
                  <li>A Tenancy Agreement (issued within the last 12 months)</li>
                </ul>
                <p>
                  Some companies may require a physical copy to be sent instead of a certified copy.
                </p>
              </div>
            </Stack>
          </Accordion>
        )}

        <Accordion
          teal
          expanded={record.willAndProbate.length !== 0}
          title={
            <RequiredHeader
              optional={!askForWillAndProbate}
              showError={!!errors.willAndProbate || record.willAndProbate.length === 0}
              title={
                serviceProvider.customForm === "monzo"
                  ? "Grant of probate, Letter of Administration or Will"
                  : "The Will"
              }
            />
          }
        >
          <Stack rowGap={1}>
            <SimpleUploadField
              busy={false}
              entries={record.willAndProbate}
              upload={updateWillAndProbate}
              remove={removeWillAndProbate}
              error={errors.willAndProbate}
              setError={(error: any) => setError("willAndProbate", error)}
            />

            <Stack rowGap={2}>
              <Typography variant="body1">
                If the person who died wrote a will, please upload images, scans or copies of each
                <b> of the pages below </b>.<b>Please do NOT </b>remove the staples in your original
                Will. Doing so{" "}
                <b>
                  {" "}
                  could cause serious issues and raise questions as to the validity of your Will.
                </b>
              </Typography>

              <ol style={{ margin: 0 }}>
                <li>the title page</li>
                <li>the page with the Executor details</li>
                <li>the signature page</li>
                {/* <li>
                    any official changes to the will, known as a <b>Codicil</b>
                  </li> */}
              </ol>

              <Typography variant="body1">
                Please note that financial institutions, such as organisations handling investments,
                will require a copy of the will.
              </Typography>

              <Typography variant="body1">
                Some companies may require a physical copy to be sent instead of a certified copy.
              </Typography>
            </Stack>
          </Stack>
        </Accordion>

        {(askForGrantOfRepresentation || showAGOROptional) && (
          <Accordion
            expanded={record.grantOfRepresentation.length !== 0}
            teal
            title={
              <RequiredHeader
                optional={showAGOROptional}
                showError={
                  !!errors.grantOfRepresentation || record.grantOfRepresentation.length === 0
                }
                title="Grant of Representation"
              />
            }
          >
            <Stack rowGap={1}>
              <SimpleUploadField
                busy={false}
                entries={record.grantOfRepresentation}
                error={errors.grantOfRepresentation}
                upload={updateGrantOfRepresentation}
                remove={removeGrantOfRepresentation}
                setError={(error: any) => setError("grantOfRepresentation", error)}
              />

              <Typography variant="body1">
                If you have received a Grant of Representation (<b>Grant of Probate</b>,{" "}
                <b>Letters of Administration</b> or <b>Certificate of Confirmation</b>) please
                upload a scan of the <b>certified copy</b>. Please note that all Executors - where
                applicable - will be required to sign any instruction.Some providers may allow a
                physical copy to be sent instead of a certified copy.
              </Typography>
            </Stack>
          </Accordion>
        )}
        {askForProofOfBankAccount && (
          <Accordion
            expanded={record.proofOfBankAccount.length !== 0}
            teal
            title={
              <RequiredHeader
                showError={!!errors.proofOfBankAccount || record.proofOfBankAccount.length === 0}
                title="Proof of a Bank Account"
              />
            }
          >
            <Stack rowGap={1}>
              <SimpleUploadField
                busy={false}
                entries={record.proofOfBankAccount}
                error={errors.proofOfBankAccount}
                upload={updateProofOfBankAccount}
                remove={removeProofOfBankAccount}
                setError={(error: any) => setError("proofOfBankAccount", error)}
              />

              <Typography variant="body1">
                Please upload a<b> void cheque </b> or <b>bank statement</b>, if taking a withdrawal
                to a personal bank account
              </Typography>
            </Stack>
          </Accordion>
        )}
      </Stack>

      <Snackbar
        sx={{ top: "58px" }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={!!remoteError}
        autoHideDuration={6000}
        onClose={removeRemoteError}
      >
        <Alert elevation={6} variant="filled" severity="error" onClose={removeRemoteError}>
          {remoteError}
        </Alert>
      </Snackbar>
    </Stack>
  );
};
